import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS, API_BASE_URL } from '../../constants/api-urls';
import { buildInspectionReportLocation, getP2pAssignment, isInspectionActiveWork, isP2pWaitingForBuyerPayment, normalizeInspectionStatus } from '../../utils/qaInspectionFlow';
import './QADashboard.scss';

const QA_MY_INSPECTIONS_URL = API_URLS.QA?.GET_MY_INSPECTIONS || '/qa/inspector/my-inspections';
const API_BASE_URL_MEDIA = API_BASE_URL.replace('/api/v1', '');

const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return '';
  if (typeof accessPath === 'string' && (accessPath.startsWith('http://') || accessPath.startsWith('https://'))) return accessPath;
  const baseUrl = API_BASE_URL_MEDIA;
  const path = typeof accessPath === 'string' ? accessPath : (accessPath && accessPath.path ? accessPath.path : '');
  if (!path) return '';
  if (path.startsWith('/auction-media/')) return `${baseUrl}${path}`;
  const fileName = path.split(/[/\\]/).pop();
  if (fileName && auctionId) return `${baseUrl}/auction-media/${auctionId}/${fileName}`;
  return `${baseUrl}/media/${fileName || ''}`;
};

export default function QAInspectionsList() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingId, setStartingId] = useState(null);

  const fetchInspections = useCallback(async () => {
    try {
      setLoading(true);
      const [auctionRes, qaRes] = await Promise.all([
        CommonService.get(API_URLS.AUCTION.GET_MY_INSPECTIONS).catch((e) => {
          console.warn('Auction inspections fetch failed:', e?.response?.status, e?.message);
          return { success: false, data: [] };
        }),
        CommonService.get(QA_MY_INSPECTIONS_URL).catch((e) => {
          console.warn('QA inspections fetch failed:', e?.response?.status, e?.message, e?.response?.data);
          return { success: false, data: { inspections: [] } };
        }),
      ]);

      const auctionList = Array.isArray(auctionRes?.data) ? auctionRes.data : [];

      const qaPayload = qaRes?.data ?? {};
      const qaList = Array.isArray(qaPayload)
        ? qaPayload
        : Array.isArray(qaPayload.inspections)
          ? qaPayload.inspections
          : Array.isArray(qaRes?.data?.inspections)
            ? qaRes.data.inspections
            : [];

      const isCmQaInspection = (ins) =>
        ins.context_source === 'contract_manufacturer' ||
        (ins.cm_rfq_id != null &&
          Number(ins.cm_rfq_id) > 0 &&
          ins.cm_quotation_id != null &&
          Number(ins.cm_quotation_id) > 0);

      const qaNormalized = (qaList || []).map((ins) => {
        const isCm = isCmQaInspection(ins);
        const scope = ins.scope_logistics && typeof ins.scope_logistics === 'object' ? ins.scope_logistics : {};
        const nameFromScope =
          (typeof scope.product_name === 'string' && scope.product_name.trim()) ||
          (typeof scope.product_description === 'string' && scope.product_description.trim()) ||
          '';
        const productTitle =
          (typeof ins.product_name === 'string' && ins.product_name.trim()) ||
          nameFromScope ||
          'QA Inspection Service';
        const rfqLabel = isCm
          ? `CM${ins.cm_manufacturer_name ? ` · ${ins.cm_manufacturer_name}` : ''} · QA-${ins.qa_request_id || ins.inspection_id}`
          : `QA-${ins.qa_request_id || ins.inspection_id}`;

        return {
          ...ins,
          _source: 'qa',
          _isCmQa: isCm,
          product_name: productTitle,
          rfq_number: rfqLabel,
          preferred_time: ins.inspection_time,
          inspection_fee: ins.inspection_fee,
          currency: ins.currency || 'USD',
        };
      });

      const combined = [...auctionList, ...qaNormalized];
      const active = combined.filter((ins) => {
        const status = normalizeInspectionStatus(ins);
        if (status === 'completed' || ins.completed_at) return false;
        if (isP2pWaitingForBuyerPayment(ins)) return false;
        return true;
      });
      setInspections(active);
    } catch (error) {
      console.error('Error fetching inspections:', error);
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInspections();
    const onFocus = () => fetchInspections();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchInspections]);

  const getP2pAssignmentForInsp = (inspection) => getP2pAssignment(inspection);

  const isP2pInspection = (inspection) => getP2pAssignmentForInsp(inspection)?.is_p2p_linked === true;

  const handleAcceptAssignment = (inspection) => {
    navigate(`/qa/inspection/${inspection.inspection_id}/assignment`);
  };

  const handleStartInspection = async (inspection) => {
    if (normalizeInspectionStatus(inspection) !== 'scheduled') return;
    const isQA = inspection._source === 'qa';
    const assignment = getP2pAssignmentForInsp(inspection);

    if (isQA && isP2pInspection(inspection)) {
      if (!assignment?.can_start_inspection) {
        toast.info('Waiting for buyer payment approval and payment before starting the inspection.');
        return;
      }
      try {
        setStartingId(inspection.inspection_id);
        const response = await CommonService.post(
          API_URLS.QA.INSPECTION_START(inspection.inspection_id),
          {},
        );
        const ok = response && (response.success === true || response.data !== undefined);
        if (ok) {
          toast.success('Inspection started');
          navigate(buildInspectionReportLocation(inspection.inspection_id, inspection));
        } else {
          throw new Error(response?.message || 'Failed to start inspection');
        }
      } catch (error) {
        console.error('Error starting inspection:', error);
        toast.error(error?.response?.data?.error || error?.message || 'Failed to start inspection');
      } finally {
        setStartingId(null);
      }
      return;
    }

    if (isQA) {
      navigate(`/qa/inspection/${inspection.inspection_id}/assignment`);
      return;
    }

    try {
      setStartingId(inspection.inspection_id);
      const response = await CommonService.put(API_URLS.AUCTION.START_INSPECTION(inspection.inspection_id), {});
      const ok = response && (response.success === true || response.data !== undefined);
      if (ok) {
        toast.success('Inspection started');
        navigate(buildInspectionReportLocation(inspection.inspection_id, inspection));
      } else {
        throw new Error(response?.message || 'Failed to start inspection');
      }
    } catch (error) {
      console.error('Error starting inspection:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to start inspection');
    } finally {
      setStartingId(null);
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    if (amount == null) return currency === 'USD' ? '$0' : '₹0';
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
    }
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getScheduledLabel = (insp) => {
    if (insp._source === 'qa' && (insp.preferred_start_date || insp.preferred_end_date)) {
      const start = insp.preferred_start_date ? formatDate(insp.preferred_start_date) : '—';
      const end = insp.preferred_end_date ? formatDate(insp.preferred_end_date) : start;
      const time = insp.preferred_time || insp.inspection_time || '—';
      const timeStr = typeof time === 'string' && time.includes(':') ? time.slice(0, 5) : time;
      return `${start}${end !== start ? ` – ${end}` : ''} at ${timeStr}`;
    }
    return `${formatDate(insp.inspection_date)} at ${insp.inspection_time || insp.preferred_time || '—'}`;
  };

  const getStatusBadge = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    const map = {
      scheduled: { label: 'Scheduled', className: 'badge-scheduled' },
      in_progress: { label: 'In Progress', className: 'badge-progress' },
      completed: { label: 'Completed', className: 'badge-quoted' },
      cancelled: { label: 'Cancelled', className: 'badge-pending' },
    };
    const s = map[normalized] || { label: status || 'N/A', className: 'badge-pending' };
    return <span className={`badge ${s.className}`}>{s.label}</span>;
  };

  const getP2pStatusBadge = (insp) => {
    const p = getP2pAssignmentForInsp(insp);
    if (!p?.is_p2p_linked || isInspectionActiveWork(insp)) return null;
    if (p.payment_approval_pending) {
      return <span className="badge badge-pending">Buyer payment approval pending</span>;
    }
    if (isP2pWaitingForBuyerPayment(insp)) {
      return <span className="badge badge-pending">Awaiting buyer payment</span>;
    }
    if (p.can_start_inspection && normalizeInspectionStatus(insp) === 'scheduled') {
      return <span className="badge badge-progress">Ready to start</span>;
    }
    return null;
  };

  const getFirstImage = (inspection) => {
    const images = inspection.images;
    if (!images) return null;
    const arr = Array.isArray(images) ? images : (typeof images === 'string' ? (images.trim().startsWith('[') ? JSON.parse(images) : [images]) : []);
    const first = arr[0];
    if (!first) return null;
    const path = typeof first === 'string' ? first : (first && first.path ? first.path : first);
    return getMediaUrl(path, inspection.auction_id);
  };

  const renderFooterActions = (insp) => {
    const status = normalizeInspectionStatus(insp);
    if (status !== 'scheduled' && status !== 'in_progress' && status !== 'completed') {
      return null;
    }

    if (isP2pInspection(insp)) {
      const p = getP2pAssignmentForInsp(insp);
      if (status === 'in_progress' || status === 'completed' || isInspectionActiveWork(insp)) {
        return (
          <button
            type="button"
            className="btn-view-quotation"
            onClick={() => navigate(buildInspectionReportLocation(insp.inspection_id, insp))}
          >
            {status === 'completed' ? 'View Report' : 'Continue Inspection'}
          </button>
        );
      }
      if (status === 'scheduled' && !p?.job_accepted_at) {
        return (
          <button
            type="button"
            className="btn-submit-quotation"
            onClick={() => handleAcceptAssignment(insp)}
          >
            Accept Assignment
          </button>
        );
      }
      if (status === 'scheduled' && p?.payment_approval_pending) {
        return (
          <p className="qa-inspection-approval-pending-msg">
            Buyer payment approval is in progress. Start Inspection will appear after approval and buyer payment are complete.
          </p>
        );
      }
      if (status === 'scheduled' && p?.can_start_inspection) {
        return (
          <button
            type="button"
            className="btn-submit-quotation"
            disabled={startingId === insp.inspection_id}
            onClick={() => handleStartInspection(insp)}
          >
            {startingId === insp.inspection_id ? 'Starting…' : 'Start Inspection'}
          </button>
        );
      }
      return null;
    }

    if (insp.status === 'scheduled') {
      return (
        <button
          type="button"
          className="btn-submit-quotation"
          disabled={startingId === insp.inspection_id}
          onClick={() => handleStartInspection(insp)}
        >
          {startingId === insp.inspection_id ? 'Starting…' : 'Start Inspection'}
        </button>
      );
    }

    if (insp.status === 'in_progress' || insp.status === 'completed') {
      return (
        <button
          type="button"
          className="btn-view-quotation"
          onClick={() => navigate(buildInspectionReportLocation(insp.inspection_id, insp))}
        >
          {insp.status === 'in_progress' ? 'Continue Inspection' : 'View Report'}
        </button>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="qa-dashboard loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="qa-dashboard">
      <div className="container-fluid">
        <div className="page-header">
          <button type="button" className="back-btn" onClick={() => navigate('/auction/qa/dashboard')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Dashboard
          </button>
          <div className="header-content">
            <h1>My Inspections</h1>
            <p>
              Inspections assigned after the buyer accepted your quotation — supplier RFQ QA and contract-manufacturing
              (CM) QA.
            </p>
          </div>
        </div>

        <div className="rfqs-list">
          {inspections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M32 8C18.745 8 8 18.745 8 32C8 45.255 18.745 56 32 56C45.255 56 56 45.255 56 32C56 18.745 45.255 8 32 8ZM32 52C21.514 52 12 42.486 12 32C12 21.514 21.514 12 32 12C42.486 12 52 21.514 52 32C52 42.486 42.486 52 32 52Z" fill="currentColor"/>
                  <path d="M32 20V32M32 36H32.02" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No Inspections Yet</h3>
              <p>
                When a buyer accepts your quotation (classic supplier RFQ or contract-manufacturing RFQ), the inspection
                appears here. You can then start the inspection and submit the report.
              </p>
            </div>
          ) : (
            inspections.map((insp) => {
              const imgUrl = getFirstImage(insp);
              return (
                <div key={insp.inspection_id} className="rfq-card">
                  <div className="rfq-card-header">
                    <div className="rfq-info">
                      <div className="rfq-id">RFQ #{insp.rfq_number} · Inspection #{insp.inspection_id}</div>
                      {insp._isCmQa && (
                        <span className="qa-flow-badge qa-flow-badge--cm" title="Quality inspection tied to a contract-manufacturer RFQ">
                          CM QA
                        </span>
                      )}
                      {getStatusBadge(insp.status)}
                      {getP2pStatusBadge(insp)}
                    </div>
                    <div className="rfq-date">Scheduled: {getScheduledLabel(insp)}</div>
                  </div>

                  <div className="rfq-card-body">
                    <div className="product-section">
                      <div className="product-image">
                        {imgUrl ? (
                          <img src={imgUrl} alt={insp.product_name} />
                        ) : (
                          <div className="placeholder-image">No Image</div>
                        )}
                      </div>
                      <div className="product-details">
                        <h3 className="product-name">{insp.product_name || 'Product'}</h3>
                        <div className="product-meta">
                          {insp.category_name && (
                            <span className="category">
                              {insp.category_name}
                            </span>
                          )}
                          {insp.inspection_fee != null && (
                            <span className="bid-amount">
                              Fee: {formatCurrency(insp.inspection_fee, insp.currency)}
                            </span>
                          )}
                        </div>
                        {insp.product_description && (
                          <p className="product-description">{String(insp.product_description).substring(0, 150)}...</p>
                        )}
                      </div>
                    </div>

                    <div className="inspection-details">
                      <div className="detail-item">
                        <div className="detail-content">
                          <div className="detail-label">Inspection Location</div>
                          <div className="detail-value">
                            {insp.inspection_location_address}
                            {insp.inspection_location_city || insp.inspection_location_state ? (
                              <><br />{insp.inspection_location_city}, {insp.inspection_location_state}</>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rfq-card-footer">
                    {renderFooterActions(insp)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
